/**
 * Color utility functions for generating color ranges and variations
 */

/**
 * Represents a collection of color shades with utility methods for color assignment
 */
export class ColorShades {
  public readonly shades: string[];
  private readonly baseColor: string;
  private readonly assignments = new Map<string, number>();

  private constructor(
    baseColor: string,
    count: number,
    lightnessRange: [number, number],
  ) {
    this.baseColor = baseColor;
    this.shades = this.generateShades(baseColor, count, lightnessRange);
  }

  private generateShades(
    baseColor: string,
    count: number,
    lightnessRange: [number, number],
  ): string[] {
    const baseHsl = hexToHsl(this.baseColor);
    const shades: string[] = [];

    if (count <= 1) {
      // If count is 1 or less, just return the base color
      shades.push(baseColor);
    } else {
      for (let index = 0; index < count; index++) {
        // Always maintain the same hue and saturation as the base color
        const hue = baseHsl.hue;
        const saturation = baseHsl.saturation;

        // Only vary lightness
        const lightnessProgress = index / (count - 1);
        const lightness =
          lightnessRange[0] +
          (lightnessRange[1] - lightnessRange[0]) * lightnessProgress;

        shades.push(hslToHex(hue, saturation, lightness));
      }
    }

    return shades;
  }

  static fromHex(
    baseColor: string,
    count: number,
    lightnessRange: [number, number] = [20, 80],
  ): ColorShades {
    return new ColorShades(baseColor, count, lightnessRange);
  }

  /**
   * Gets a color by index, wrapping around if the index exceeds the number of shades
   * @param index - The index of the color to retrieve
   * @returns A hex color string
   */
  public getColor(index: number): string {
    return this.shades.at(index % this.shades.length) ?? this.baseColor;
  }

  /**
   * Gets a consistent color for a given ID, ensuring the same ID always gets the same color
   * @param id - The identifier to get a consistent color for
   * @returns A hex color string
   */
  public getById(id: string): string {
    let colorIndex = this.assignments.get(id);
    if (colorIndex === undefined) {
      colorIndex = this.assignments.size;
      this.assignments.set(id, colorIndex);
    }
    return this.getColor(colorIndex);
  }

  /**
   * Pre-prime the color assignments for a list of IDs
   * @param ids - The identifiers to prime the color assignments for
   */
  public primeIds(ids: string[]): void {
    for (const id of ids) this.getById(id);
  }
}

/**
 * Generates color shades by varying lightness while keeping hue and saturation constant
 * @param baseColor - Hex color to generate shades from
 * @param count - Number of shades to generate
 * @param lightnessRange - Lightness range (0-100), defaults to [20, 80]
 * @returns ColorShades instance with utility methods for color assignment
 * @example generateColorShades('#d32f2f', 4).getColor(5) // Returns colors[1] (wrapped)
 * @example generateColorShades('#d32f2f', 4).getById('user123') // Returns consistent color for 'user123'
 */

// Internal helper function: Converts hex to HSL
const hexToHsl = (() => {
  const calculateHue = (
    red: number,
    green: number,
    blue: number,
    max: number,
    delta: number,
  ): number => {
    switch (max) {
      case red:
        return ((green - blue) / delta) % 6;
      case green:
        return (blue - red) / delta + 2;
      case blue:
        return (red - green) / delta + 4;
      default:
        return 0;
    }
  };

  const calculateSaturation = (
    max: number,
    min: number,
    lightness: number,
  ): number => {
    if (max === min) return 0;
    const delta = max - min;
    return lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  };

  return (
    hex: string,
  ): { hue: number; saturation: number; lightness: number } => {
    // Remove # if present
    const cleanHex = cleanHexString(hex);

    // Parse RGB values
    const [red, green, blue] = hexToRgbArray(cleanHex);

    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    const lightness = (max + min) / 2;
    const saturation = calculateSaturation(max, min, lightness);
    const hue =
      max !== min ? calculateHue(red, green, blue, max, max - min) / 6 : 0;

    return {
      hue: hue * 360,
      saturation: saturation * 100,
      lightness: lightness * 100,
    };
  };
})();

// Internal helper function: Converts HSL to hex
const hslToHex = (() => {
  const hue2rgb = (
    primary: number,
    secondary: number,
    tertiary: number,
  ): number => {
    if (tertiary < 0) tertiary += 1;
    if (tertiary > 1) tertiary -= 1;
    if (tertiary < 1 / 6) return primary + (secondary - primary) * 6 * tertiary;
    if (tertiary < 1 / 2) return secondary;
    if (tertiary < 2 / 3)
      return primary + (secondary - primary) * (2 / 3 - tertiary) * 6;
    return primary;
  };

  const toHex = (colorComponent: number): string => {
    const hex = Math.round(colorComponent * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return (hue: number, saturation: number, lightness: number): string => {
    const normalizedHue = hue / 360;
    const normalizedSaturation = saturation / 100;
    const normalizedLightness = lightness / 100;

    let red: number;
    let green: number;
    let blue: number;

    if (normalizedSaturation === 0) {
      red = green = blue = normalizedLightness; // achromatic
    } else {
      const secondary =
        normalizedLightness < 0.5
          ? normalizedLightness * (1 + normalizedSaturation)
          : normalizedLightness +
            normalizedSaturation -
            normalizedLightness * normalizedSaturation;
      const primary = 2 * normalizedLightness - secondary;
      red = hue2rgb(primary, secondary, normalizedHue + 1 / 3);
      green = hue2rgb(primary, secondary, normalizedHue);
      blue = hue2rgb(primary, secondary, normalizedHue - 1 / 3);
    }

    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
  };
})();

const cleanHexString = (hex: string): string => {
  let clean = hex.replace('#', '');
  if (clean.length === 3) {
    const r = clean.charAt(0);
    const g = clean.charAt(1);
    const b = clean.charAt(2);
    return r + r + g + g + b + b;
  }
  return clean;
};

export type RGBColor = [number, number, number];

/**
 * Converts a hex color string to an RGB array [r, g, b] where components are 0-1
 * @param hex - Hex color string (with or without #)
 * @returns RGB array [r, g, b]
 */
export const hexToRgbArray = (hex: string): RGBColor => {
  let cleanHex = cleanHexString(hex);
  const red = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const green = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const blue = parseInt(cleanHex.substring(4, 6), 16) / 255;
  return [red, green, blue];
};
